import React, { FunctionComponent } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { UserRolesEnum } from '../../../support/enums/UserRoles.Enum';
import { AccessControlSecurityProfiles } from '../access-control-security-profiles/AccessControlSecurityProfiles.Component';
import { TopNavigation } from '../top-navigation/TopNavigation.Component';
import styles from './AccessControlController.Styles';
import { ReactComponent as AccessControlIcon } from '../../../assets/svgs/lastAccessControl.svg';
import { BreadCrumb } from '../../../support/custom-hooks/useBreadCrum';
import AccessControl, { checkPermissions } from '../../generic-components/access-control/AccessControl.Component';
import { IAdminNavigationCard } from '../admin-navigation/NavigationCards';
import { useSelector } from 'react-redux';
import { UsersState } from '../../../store/reducers/usersReducer';
import {
    accessControlRules,
    activityLogRules,
    adminRules,
    securityProfilesRules,
    usersRules,
} from '../../../support/constants/SecurityRules.Constants';
import { AccessControlUsers } from '../access-control-users/AccessControlUsers.Component';
import { AccessControlActivityLog } from '../access-control-activity-log/AccessControlActivityLog.Component';

interface AccessControlControllerProps {
    initialTabIndex: number;
    profileId?: number;
}

export const AccessControlController: FunctionComponent<AccessControlControllerProps> = ({
    initialTabIndex,
    profileId,
}) => {
    const classes = styles();
    const [currentSubroutIndex, setCurrentSubroutIndex] = React.useState('');
    const { path } = useRouteMatch();
    const history = useHistory();
    const location = useLocation();
    const userSecurityRules = useSelector<UsersState, UsersState['securityRules']>((state) => state.securityRules);

    const handleTabChange = (newTab: string) => {
        setCurrentSubroutIndex(newTab);
    };

    const usersPermissions = [...adminRules.read, ...accessControlRules.read, ...usersRules.read];
    const accessControlSecurityProfilesPermissions = [
        ...adminRules.read,
        ...accessControlRules.read,
        ...securityProfilesRules.read,
    ];
    // checkPermissions matches with .some(), so listing the parent rules here would grant the tab to
    // anyone with read:ADMIN. The AC requires the explicit Activity Log grant.
    const activityLogPermissions = [...activityLogRules.read];
    const accessControlDefaultTabPath = '/admin/access-control/users';

    const getAccessControlLandingLink = (): string => {
        if (checkPermissions(userSecurityRules, usersPermissions, false)) {
            return accessControlDefaultTabPath;
        }
        if (checkPermissions(userSecurityRules, accessControlSecurityProfilesPermissions, false)) {
            return '/admin/access-control/security-profiles';
        }
        if (checkPermissions(userSecurityRules, activityLogPermissions, false)) {
            return '/admin/access-control/activity-log';
        }
        return '/admin';
    };

    React.useEffect(() => {
        const isBareAccessControlPath =
            location.pathname === '/admin/access-control' || location.pathname === '/admin/access-control/';

        if (isBareAccessControlPath) {
            history.replace(getAccessControlLandingLink());
        }
    }, [history, location.pathname, userSecurityRules]);

    const card: IAdminNavigationCard = {
        order: 1,
        section: null,
        subRoutes: [
            { label: 'Users', link: '/admin/access-control/users', allowedPermissions: usersPermissions },
            {
                label: 'Security Profiles',
                link: '/admin/access-control/security-profiles',
                allowedPermissions: accessControlSecurityProfilesPermissions,
            },
            {
                label: 'Activity Log',
                link: '/admin/access-control/activity-log',
                allowedPermissions: activityLogPermissions,
            },
        ],
        subRoutesRoles: [
            //Users
            [UserRolesEnum.Admin, UserRolesEnum.TcMainAdmin],
            //Security Profiles
            [UserRolesEnum.Admin, UserRolesEnum.TcMainAdmin],
            //Activity Log
            [UserRolesEnum.Admin, UserRolesEnum.TcMainAdmin],
        ],
        icon: <AccessControlIcon />,
        title: 'Access Control',
        link: getAccessControlLandingLink(),
        allowedPermissions: [],
        hidden: false,
        showTabs: true,
    };

    const adminBreadCrumb = {
        name: 'Admin',
        goBack: false,
        link: '/admin',
        path: path,
    };

    const accessControlBreadCrumb = {
        name: 'Access Control',
        goBack: false,
        link: getAccessControlLandingLink(),
        path: path,
    };

    const usersBreadCrumb = {
        name: 'Users',
        goBack: false,
        link: '/admin/access-control/users',
        path: path,
    };

    const securityProfilesBreadCrumb = {
        name: 'Security Profiles',
        goBack: false,
        link: '/admin/access-control/security-profiles',
        path: path,
    };

    const activityLogBreadCrumb = {
        name: 'Activity Log',
        goBack: false,
        link: '/admin/access-control/activity-log',
        path: path,
    };

    return (
        <div className={classes.selectedComponentContainerWithTabs}>
            <TopNavigation
                {...card}
                tabIndex={currentSubroutIndex}
                handleTabChange={handleTabChange}
                initialTabIndex={initialTabIndex}
                profileId={profileId}
            />
            <Switch>
                <Route
                    exact
                    path="/admin/access-control"
                    render={() => <Redirect to={getAccessControlLandingLink()} />}
                />
                <Redirect
                    exact
                    from="/admin/access-control/users/security-profiles"
                    to="/admin/access-control/security-profiles"
                />
                <Redirect
                    exact
                    from="/admin/access-control/users/activity-log"
                    to="/admin/access-control/activity-log"
                />
                <Route path="/admin/access-control/users">
                    <AccessControl allowedPermissions={usersPermissions} sendBack={false}>
                        <Route path="/admin/access-control/users" exact>
                            <div className={classes.breadCrumb}>
                                <BreadCrumb sections={[adminBreadCrumb, accessControlBreadCrumb, usersBreadCrumb]} />
                            </div>
                        </Route>
                        <AccessControlUsers />
                    </AccessControl>
                </Route>
                <Route path="/admin/access-control/security-profiles" exact>
                    <AccessControl allowedPermissions={accessControlSecurityProfilesPermissions} sendBack={false}>
                        <div className={classes.breadCrumb}>
                            <BreadCrumb
                                sections={[adminBreadCrumb, accessControlBreadCrumb, securityProfilesBreadCrumb]}
                            />
                        </div>
                        <AccessControlSecurityProfiles />
                    </AccessControl>
                </Route>
                <Route path="/admin/access-control/activity-log" exact>
                    <AccessControl allowedPermissions={activityLogPermissions} sendBack={false}>
                        <div className={classes.breadCrumb}>
                            <BreadCrumb sections={[adminBreadCrumb, accessControlBreadCrumb, activityLogBreadCrumb]} />
                        </div>
                        <AccessControlActivityLog />
                    </AccessControl>
                </Route>
            </Switch>
        </div>
    );
};
